import classNames from 'classnames'
import React from 'react'

export type LinkAttachmentVariant = 'dark' | 'light'

export interface CardShellProps {
  variant: LinkAttachmentVariant
  children: React.ReactNode
  /**
   * When provided, the entire card chrome is rendered as an anchor (used
   * by Sent / Received cards to open the link target on click). Falls
   * back to a `<div>` when omitted so Composer cards stay
   * non-navigational while drafting.
   */
  href?: string
  /**
   * Click handler for the card chrome. When `href` is set the shell is an
   * anchor and `onClick` is invoked in addition to navigation. When `href`
   * is omitted but `onClick` is set, the shell renders as a clickable
   * button (used by media-only Received cards to open an image preview).
   */
  onClick?: () => void
  /** Accessible label for the clickable variant (when `onClick` is set without `href`). */
  ariaLabel?: string
  rootRef?: React.Ref<HTMLElement>
  /**
   * Absolutely-positioned slot rendered in the top-right corner of the
   * shell. Used by the Composer card to surface its dismiss affordance
   * when there's no hero thumbnail to anchor it to.
   */
  topRight?: React.ReactNode
  /**
   * Overrides the variant-derived background colour (e.g. audio cards
   * use `bg-[#F2F3F4]` regardless of the dark/light variant).
   */
  bgClassName?: string
  /**
   * Raw extracted accent colour, published as the `--accent` custom property.
   * `styles.css` derives the card surface from it (`oklch(from …)`) and paints
   * it over the `bg-[…]` fallback where the browser supports it. `undefined`
   * keeps the variant / `bgClassName` fill.
   */
  accentHex?: string
  /**
   * Locks the card to Figma's 250px box and makes it a flex column, so the
   * hero absorbs whatever height the chin leaves. Only the `featured` layout
   * is drawn at a fixed height — `classic` cards and the audio-player card
   * size to their content.
   */
  fixedHeight?: boolean
  /**
   * Renders the shell as a labelled busy region while the link preview is
   * still being fetched. `role="status"` is what makes `aria-label` legal
   * (and announced) on an otherwise generic container.
   */
  busy?: boolean
  'data-testid'?: string
}

/**
 * Accessible name for the loading skeleton. Hardcoded English, matching the
 * toolkit's existing convention (`aria-label="Dismiss attachment"`) — there
 * is no i18n layer here.
 */
export const LOADING_LABEL = 'Loading link preview'

const SHELL_CLASS = classNames(
  // Width is a channel-scoped token: default 290px, but a read-only surface with no
  // composer (the official channel) sets `--lt-msg-card-width` on its root to widen the
  // content column (see ChannelView). Consumers that don't set the var keep 290px.
  // `max-w-full` caps the card to its container so a widened value can never overflow
  // horizontally on a narrow (sub-393px) viewport — it shrinks to fit instead.
  'relative w-[var(--lt-msg-card-width,290px)] max-w-full select-none overflow-hidden',
  // 20px corners — no token exists for it in the component-library preset,
  // whose radius scale is sm .5rem / md 1rem / lg 1.5rem / xl 4rem.
  'rounded-[20px]'
)

// The 290x250 lock: the chin takes the height its 1-2 line title needs
// (82px at two lines, 62px at one) and the hero absorbs the remainder.
const FIXED_HEIGHT_CLASS = 'flex h-[250px] flex-col'

/**
 * Outer chrome for every `LinkAttachment.*` card. Matches the 290px x 250px
 * box, 20px corner radius, and borderless / shadowless treatment of the
 * composer and sent instances in Figma.
 */
const CardShell: React.FC<CardShellProps> = ({
  variant,
  children,
  href,
  onClick,
  ariaLabel,
  rootRef,
  topRight,
  bgClassName,
  accentHex,
  fixedHeight = false,
  busy = false,
  'data-testid': dataTestId,
}) => {
  const isInteractive = href != null || onClick != null
  const className = classNames(
    'messaging-link-card',
    SHELL_CLASS,
    fixedHeight ? FIXED_HEIGHT_CLASS : 'block',
    // The plain fallback fill, kept underneath the `--accent` derivation: the
    // `.messaging-link-card[style*='--accent']` rule in `styles.css` outranks
    // this single class, so a published accent paints over it and an unset one
    // lands on this surface.
    //
    // Must be an arbitrary value, not a theme token: this package ships no
    // Tailwind utilities, so consumers generate our classes by scanning
    // `messaging-react/dist` against their own theme. A theme colour a consumer
    // lacks compiles to nothing and leaves the chin transparent; an arbitrary
    // value is generated from the class name alone.
    //
    // Both values are the plain message bubble's own fill, so a card with no
    // usable accent matches the text bubbles around it: `#1e2330` is
    // `--str-chat__own-message-bubble-background-color` and `#f1f0ee` is
    // `--str-chat__message-bubble-background-color`, mirrored by the bubble
    // tail's own fills in `styles.css`.
    bgClassName ?? (variant === 'dark' ? 'bg-[#1e2330]' : 'bg-[#f1f0ee]'),
    // `focus-ring` is a design-system utility from the component-library
    // tailwind preset — outline-none + a black 2px focus-visible ring
    // with offset, so keyboard users can see the focused card.
    isInteractive ? 'cursor-pointer no-underline focus-ring' : null
  )

  const busyProps = busy
    ? { role: 'status', 'aria-busy': true, 'aria-label': LOADING_LABEL }
    : undefined

  // Published as a custom property, not a `background`: the `[style*='--accent']`
  // rule in `styles.css` keys off its presence and derives the surface in CSS.
  const accentStyle = accentHex
    ? ({ '--accent': accentHex } as React.CSSProperties)
    : undefined

  const corner = topRight ? (
    <div className="pointer-events-auto absolute right-3 top-3 z-10">
      {topRight}
    </div>
  ) : null

  if (href) {
    return (
      <a
        ref={rootRef as React.Ref<HTMLAnchorElement>}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
        data-testid={dataTestId}
        data-variant={variant}
        className={className}
        style={accentStyle}
        {...busyProps}
      >
        {children}
        {corner}
      </a>
    )
  }

  if (onClick) {
    return (
      <button
        ref={rootRef as React.Ref<HTMLButtonElement>}
        type="button"
        onClick={onClick}
        aria-label={ariaLabel}
        data-testid={dataTestId}
        data-variant={variant}
        className={classNames(className, 'text-left')}
        style={accentStyle}
        {...busyProps}
      >
        {children}
        {corner}
      </button>
    )
  }

  return (
    <div
      ref={rootRef as React.Ref<HTMLDivElement>}
      data-testid={dataTestId}
      data-variant={variant}
      className={className}
      style={accentStyle}
      {...busyProps}
    >
      {children}
      {corner}
    </div>
  )
}

export default CardShell
